import { useCallback, useMemo, useState } from "react";

type RequestFn<TPayload, TResult> = (payloads: TPayload) => Promise<TResult>;

interface UseSequentialRequests<TPayload, TResult> {
    progress: number;
    total: number;
    start(
        payloads: TPayload[],
        onResult?: (result: TResult) => void,
    ): Promise<TResult[]>;
}

export function useSequentialRequest<TPayload, TResult>(
    request: RequestFn<TPayload, TResult>,
): UseSequentialRequests<TPayload, TResult> {
    const [progress, setProgress] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);

    const start = useCallback(
        async (payloads: TPayload[], onResult?: (result: TResult) => void) => {
            setTotal(payloads.length);
            const runRequests = async () => {
                const results = [];

                for (const payload of payloads) {
                    const result = await request(payload);
                    results.push(result);
                    onResult?.(result);
                    setProgress((prev) => prev + 1);
                }

                return results;
            };
            return await runRequests().finally(() => {
                setProgress(0);
                setTotal(0);
            });
        },
        [request],
    );

    return useMemo(
        () => ({
            progress,
            start,
            total,
        }),
        [progress, start, total],
    );
}
